<template>
    <ui-modal ref="modal" title="Vehicle Reservation" size="large">
        <div class="row align-items-center">
            <div class="col" >
                <div style="max-width: 100%;" class="truncate mb-1">
                    <hr class="labeled" data-content="Customer">
                    <simple-contact-input
                        v-model="contactProxy"
                        :errors="contactErrors"
                        with-address
                    ></simple-contact-input>
                </div>
            </div>
        </div>

        <div class="row align-items-center">
            <div class="col" >

                <hr class="labeled" data-content="Notes">

                <div style="max-width: 100%;" class="truncate mb-1">
                    <div style="background: #fffcdc; padding: 1.5rem 1rem .5rem; margin-bottom: .5rem;">
                        <ui-textbox
                            ref="notes"
                            multi-line
                            label=""
                            :maxlength="256"
                            enforce-maxlength
                            v-model="reservation.notes"
                            :invalid="errors.notes.isInvalid"
                            :error="errors.notes.firstError"
                            help="Visible on the reservation receipt & vehicles profile"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="row align-items-center">
            <div class="col-12">
                <hr class="labeled" data-content="Payment Details">
            </div>
            <div class="col-4">
                <ui-textbox
                    v-model="reservation.deposit_amount"
                    icon="currency_pound"
                    placeholder="Deposit Amount (e.g. 199)"
                    :invalid="errors.deposit_amount.isInvalid"
                    :error="errors.deposit_amount.firstError"
                ></ui-textbox>
            </div>
            <div class="col-4">
                <ui-select
                    v-model="paymentMethodProxy"
                    placeholder="Payment Method"
                    :options="paymentMethods"
                    :invalid="errors.payment_method.isInvalid"
                    :error="errors.payment_method.firstError"
                ></ui-select>
            </div>
            <div class="col-4">
                <ui-textbox
                    v-model="reservation.payment_reference"
                    placeholder="Payment Reference"
                    :invalid="errors.payment_reference.isInvalid"
                    :error="errors.payment_reference.firstError"
                ></ui-textbox>
            </div>
        </div>

        <div class="row align-items-center">
            <div class="col" >
                <hr class="labeled" data-content="Team Member">
                <div style="max-width: 100%;" class="truncate mb-1">
                    <keen-team-member-picker
                        v-model="salesPersonProxy"
                        :disabled="shouldBeDisabled"
                        icon="face"
                        :invalid="errors.sales_person_id.isInvalid"
                        :error="errors.sales_person_id.firstError"
                    />
                </div>
            </div>
        </div>
         <div slot="footer" class="container-fluid no-left-padding no-right-padding">
            <div class="row align-items-center">
                <div class="col text-left">
                    <ui-button
                        @click="cancel"
                        :loading="isCancelling"
                        :disabled="shouldBeDisabled || !reservationExists"
                        type="secondary"
                        color="red"
                        icon="undo"
                    >Un-reserve</ui-button>
                    <ui-button
                        @click="viewReceipt"
                        :loading="isCancelling"
                        :disabled="shouldBeDisabled || !reservationExists"
                        type="secondary"
                        color="primary"
                        icon="receipt"
                    >View Receipt</ui-button>
                </div>
                <div class="col-6 text-right">
                    <div style="display: inline-block;" class="ml-4">
                        <convert-reservation-button
                            :instance="instance"
                            :disabled="shouldBeDisabled || !reservationExists"
                            style="vertical-align: top;"
                            @converted="e => $emit('converted', e)"
                        ></convert-reservation-button>
                        <ui-button
                            @click="save"
                            :loading="isSaving"
                            :disabled="shouldBeDisabled || !isDirty"
                            color="primary"
                        >{{ this.reservationExists ? 'Save & Close' : 'Save & Print' }}</ui-button>
                    </div>
                </div>
            </div>
        </div>

        <similar-contact-modal
            ref="duplicates"
        ></similar-contact-modal>
    </ui-modal>
</template>

<script>
import {
    VehicleReservationSchema,
    VehicleReservationResource,
    VehicleReservationTypes,
    SignableReservationReceiptResource
} from "../../../classes/Api/VehicleReservation"
    import {ContactResource, ContactSchema, ContactTypes} from "../../../classes/Api/Contact"
    import KeenBasicContactInput from "../../forms/controls/KeenBasicContactInput"
    import ErrorHelpers from "../../forms/ErrorHelpers";
    import AddressSelector from '../../forms/controls/KeenAddress.vue'
    import {PaymentMethodKeyValueList, PaymentMethods} from "../../../classes/Api/Payment";
    import Bus from "../../../events";
    import ConvertReservationButton from "./ConvertReservationButton.vue";
import SimilarContactModal from "../../elements/SimilarContactModal.vue";
import SimpleContactInput from "../../contacts/SimpleContactInput.vue";
import cloneDeep from "lodash/cloneDeep";
import {paymentMethodListWithout} from "../../../../ts/Accounts/payments";

    const ErrorKeys = [
        'instance_id',
        'sales_person_id',
        'contact_id',
        'first_name',
        'last_name',
        'primary_telephone',
        'email',
        'deposit_amount',
        'payment_method',
        'payment_reference',

        'notes',
        {
            key: 'name',
            composition: [
                'first_name',
                'last_name',
                'company_name'
            ]
        },
        {
          key: 'contact',
          composition: [
              'contact_id',
              'first_name',
              'last_name',
              'primary_telephone',
              'email'
          ]
        },{
          key: 'address',
          composition: [
              'address_1',
              'address_2',
              'address_3',
              'town',
              'county',
              'post_code',
              'country'
          ]
        },
    ]

    export default {
        components: {
            SimpleContactInput,
            SimilarContactModal,
            ConvertReservationButton,
            KeenBasicContactInput,
            AddressSelector
        },
        mixins: [ErrorHelpers],
        created() {
            this.reset()
        },
        data() {
            return {
                isBusy: false,
                isSaving: false,
                isCancelling: false,
                isDirty: false,
                closeOnSave: true,
                instance: null,
                reservation: null,
                ErrorKeys,
                contactErrors: {}
            }
        },
        watch: {
            reservation: {
                handler: function(n, o) {
                    this.isDirty = true
                },
                deep: true
            }
        },
        methods: {
            viewReceipt() {
                if (this.isDirty) {
                    if (confirm('Do you want to save your changes before viewing reservation receipt?')) {
                        this.closeOnSave = false
                        this.save().then(() => {
                            this.viewReceipt()
                            this.closeOnSave = true
                        })
                        return;
                    }

                    return
                }

                Bus.emit('showSignableDocument', {
                    resource: SignableReservationReceiptResource.forReservation(this.reservation.id),
                    documentType: 'reservation_pack',
                    messageDefaults: {
                        to: this.reservation.contact
                    }
                })
            },
            reset() {
                this.isBusy = false
                this.isSaving = false
                this.instance = null
                this.reservation = null
                this.closeOnSave = true

                this.reservation = new VehicleReservationSchema({
                    type: VehicleReservationTypes.TYPE_SALESMAN,
                    contact: new ContactSchema({
                        type: ContactTypes.LEAD,
                    }),
                    sales_person: window.marque.ui.user,
                    sales_person_id: window.marque.ui.user.id,
                })

                this.isDirty = false
            },
            open(instance, reservation = null) {
                this.reset()

                let isDirty = false

                this.instance = cloneDeep(instance)

                if (reservation && typeof reservation !== 'object') {
                    this.isBusy = true
                    VehicleReservationResource.find(reservation).then(r => {
                        this.reservation = r.data
                        this.isBusy = false
                        this.isDirty = false
                    }).catch(e => { this.isBusy = false; handleApiError(e)})

                    this.reservation = reservation
                } else if (reservation) {
                    this.reservation = cloneDeep(reservation)

                    if (!this.reservationExists) {
                        isDirty = true
                    }
                }

                this.$nextTick(() => {
                    this.$refs.notes.refreshSize()

                    this.isDirty = isDirty
                })

                this.$refs.modal.open()
            },
            close() {
                this.$refs.modal.close()
            },
            save() {
                this.errors = this.makeErrorStack()
                this.contactErrors = {}

                this.isSaving = true

                let promises = []

                this.reservation.instance_id = this.instance.id

                return this.$refs.duplicates.check(this.reservation.contact)
                    .then(contact => {
                        this.reservation.contact = contact

                        if (this.reservation.contact.id) {
                            this.reservation.contact_id = this.reservation.contact.id
                        }

                        let data = cloneDeep(this.reservation)

                        if (!this.reservation.contact_id) {
                            // Save the contact
                            data.contact.referral_source_id = window.marque.ui.otherReferralSource.id
                            data.contact.type = ContactTypes.LEAD

                            promises.push(ContactResource.store(data.contact).then(response => {
                                this.reservation.contact_id = data.contact_id = response.data.id
                                this.reservation.contact = response.data
                            }).catch(e => {
                                if (e.response.status === 422) {
                                    this.contactErrors = e.response.data.errors
                                }
                            }))
                        }

                        return Promise.all(promises).then(() => {
                            return VehicleReservationResource.storeOrUpdate(this.reservation.id, this.reservation).then(r => {
                                this.$emit('saved', r.data)

                                const isNew = !this.reservationExists

                                this.reservation = cloneDeep(r.data)
                                this.isBusy = false
                                this.isDirty = false

                                if (isNew) {
                                    this.viewReceipt()
                                    this.closeOnSave = true
                                } else if (this.closeOnSave) {
                                    this.$refs.modal.close()
                                }

                            }).catch(this.handleHttpException)
                        }).finally(() => {
                            this.isSaving = false
                        })
                    }).catch(() => {
                        this.isSaving = false
                    })

            },
            cancel() {
                if (confirm('Are you sure you want to cancel the reservation?')) {
                    this.isCancelling = true

                    VehicleReservationResource.cancel(this.reservation.id).then(r => {
                        this.$emit('cancelled', r.data)

                        this.$refs.modal.close()
                    }).catch(handleApiError).finally(() => {
                        this.isCancelling = false
                    })
                }
            },
        },
        computed: {
            paymentMethodProxy: {
                get() {
                    const method = this.paymentMethods.find(
                        i => i.value === this.reservation.payment_method
                    )

                    return method ?? ''
                },
                set(value) {
                    this.reservation.payment_method = value.value
                }
            },
            paymentMethods() {
                return paymentMethodListWithout([
                    PaymentMethods.FINANCE,
                    PaymentMethods.EXISTING_CREDIT,
                    PaymentMethods.TBC
                ]).value
            },
            contactProxy: {
                get() {
                    return this.reservation.contact
                },
                set(value) {
                    this.reservation.contact = value
                    this.reservation.contact_id = value.id
                }
            },
            salesPersonProxy: {
                get() {
                    return this.reservation.sales_person
                },
                set(value) {
                    this.reservation.sales_person = value
                    this.reservation.sales_person_id = value.id
                }
            },
            reservationExists() {
                return !!this.reservation.id
            },
            shouldBeDisabled() {
                return this.isSaving || this.isBusy || this.isCancelling
            }
        }
    }
</script>

<style scoped>

</style>